/**
 * Monthly status report - derivation and prefill.
 *
 * The reason this feature lives inside TrackIT rather than in PowerPoint is
 * that TrackIT already knows most of the answers. Every RAG on the report card
 * is computed here from data the tool already holds (risk log, dates, ledger,
 * costs); the PM's job is to check four derived values and write the narrative,
 * not to fill twenty empty boxes.
 *
 * Derived values are stored alongside any override (see `RagAssessment`) so the
 * disagreement between "what the data says" and "what the PM reported" is
 * preserved rather than flattened.
 */

import type {
  Milestone,
  Project,
  ProjectReport,
  RagAssessment,
  RagStatus,
  ReportBullet,
  ReportRisk,
  ReportSnapshot,
  RiskCategory,
  RiskEntry,
  Settings,
} from '../types'
import { effectiveRiskScore, ragForScore, riskRag, DEFAULT_RISK_FORECAST_MIN_SCORE } from './risk'
import { forecastOnTime } from './utils'
import { monthKey } from './ledger'

const DAY_MS = 24 * 60 * 60 * 1000

// ── Month helpers ────────────────────────────────────────────────────────────
// Local (not UTC) so a report opened late on the 31st doesn't land in next
// month for UK/EU users - same rule as the end-of-month review page.

export const currentReportMonth = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const shiftMonth = (key: string, delta: number): string => {
  const [y, m] = key.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const monthLabel = (key: string): string => {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

export const shortMonthLabel = (key: string): string => {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

// ── RAG helpers ──────────────────────────────────────────────────────────────

export const effectiveRag = (a: RagAssessment): RagStatus => a.override ?? a.derived

const RAG_RANK: Record<RagStatus, number> = { green: 0, amber: 1, red: 2 }

export const worstRag = (rags: RagStatus[]): RagStatus =>
  rags.reduce<RagStatus>((worst, r) => (RAG_RANK[r] > RAG_RANK[worst] ? r : worst), 'green')

interface Signal {
  rag: RagStatus
  reason: string
}

/** Combine signals, keeping the reason belonging to whichever drove the result.
 *  Green signals only supply the reason when nothing worse exists, so a healthy
 *  dimension still explains itself instead of showing a bare "Green". */
function combine(signals: Signal[]): Signal {
  const rag = worstRag(signals.map(s => s.rag))
  const driver = signals.find(s => s.rag === rag)
  return { rag, reason: driver?.reason ?? '' }
}

/** Worst open risk in a category, as a RAG signal. Only risks at or above the
 *  PMO's materiality threshold count - a Green risk is genuinely low and
 *  shouldn't move a leadership status. */
function riskSignal(
  risks: RiskEntry[] | undefined,
  category: RiskCategory,
  minScore: number,
): Signal | null {
  const open = (risks ?? []).filter(
    r => r.status === 'open' && r.category === category && effectiveRiskScore(r) >= minScore,
  )
  if (open.length === 0) return null
  const worstScore = Math.max(...open.map(effectiveRiskScore))
  const band = ragForScore(worstScore)
  if (band === 'green') return null
  const label = band === 'red' ? 'Red' : 'Amber'
  const noun = open.length === 1 ? 'risk' : 'risks'
  return { rag: band, reason: `${open.length} open ${label} ${category} ${noun}` }
}

// ── Per-dimension derivation ─────────────────────────────────────────────────

/** Schedule: the project's own dates first, then the delivery risk log.
 *  A completed project is judged on what actually happened; an in-flight one on
 *  whether the clock has run out (see `forecastOnTime`). */
export function deriveSchedule(project: Project, settings: Settings): Signal {
  const onTimeMs = (settings.onTimeDays ?? 14) * DAY_MS
  const minScore = settings.riskForecastMinScore ?? DEFAULT_RISK_FORECAST_MIN_SCORE
  const signals: Signal[] = []

  if (project.status === 'complete') {
    if (project.actualGoLive && project.dueDate) {
      const slip = new Date(project.actualGoLive).getTime() - new Date(project.dueDate).getTime()
      signals.push(
        slip <= onTimeMs
          ? { rag: 'green', reason: 'Delivered within tolerance' }
          : { rag: 'red', reason: `Delivered ${Math.round(slip / DAY_MS)} days late` },
      )
    } else {
      signals.push({ rag: 'green', reason: 'Delivered' })
    }
  } else {
    const { late, reason } = forecastOnTime(project, onTimeMs)
    if (late && reason === 'overdue') {
      const days = Math.round((Date.now() - new Date(project.dueDate).getTime()) / DAY_MS)
      signals.push({ rag: 'red', reason: `${days} days past due date` })
    } else if (late && reason === 'planned-late') {
      const slip = Math.round(
        (new Date(project.actualGoLive!).getTime() - new Date(project.dueDate).getTime()) / DAY_MS,
      )
      signals.push({ rag: 'amber', reason: `Go-live booked ${slip} days late` })
    } else {
      signals.push({ rag: 'green', reason: 'On plan for due date' })
    }
  }

  const risk = riskSignal(project.risks, 'delivery', minScore)
  if (risk) signals.push(risk)
  return combine(signals)
}

/** Scope: delivered-in-full for completed work, open scope risks otherwise. */
export function deriveScope(project: Project, settings: Settings): Signal {
  const minScore = settings.riskForecastMinScore ?? DEFAULT_RISK_FORECAST_MIN_SCORE
  const signals: Signal[] = []

  if (project.status === 'complete' && project.deliveredInFull === 'N') {
    signals.push({ rag: 'red', reason: 'Not delivered in full' })
  } else {
    signals.push({ rag: 'green', reason: 'No open scope risks' })
  }

  const risk = riskSignal(project.risks, 'scope', minScore)
  if (risk) signals.push(risk)
  return combine(signals)
}

/** Cost: actual-vs-forecast against the PMO's At Cost tolerance, plus the cost
 *  risk log. Over forecast but inside tolerance is amber, not red - the whole
 *  point of the tolerance is that small overruns aren't a leadership issue. */
export function deriveCost(project: Project, settings: Settings): Signal {
  const atCostPct = settings.atCostPct ?? 10
  const minScore = settings.riskForecastMinScore ?? DEFAULT_RISK_FORECAST_MIN_SCORE
  const signals: Signal[] = []

  if (project.actualCost != null && project.forecastCost > 0) {
    const ceiling = project.forecastCost * (1 + atCostPct / 100)
    const overPct = Math.round(((project.actualCost - project.forecastCost) / project.forecastCost) * 100)
    if (project.actualCost > ceiling) {
      signals.push({ rag: 'red', reason: `${overPct}% over forecast cost` })
    } else if (project.actualCost > project.forecastCost) {
      signals.push({ rag: 'amber', reason: `${overPct}% over, within tolerance` })
    } else {
      signals.push({ rag: 'green', reason: 'Within forecast cost' })
    }
  } else {
    signals.push({ rag: 'green', reason: 'No cost overrun recorded' })
  }

  const risk = riskSignal(project.risks, 'cost', minScore)
  if (risk) signals.push(risk)
  return combine(signals)
}

/**
 * Benefit: realised hours against the plan, both taken from the monthly ledger.
 *
 * `base` is the auto-seeded plan and `adjustments` is the owner's correction,
 * so summing each up to the current month gives "what we said we'd bank" versus
 * "what we actually banked" - the cleanest benefit signal the tool holds.
 *
 * Projects with no ledger (anything still in forecast) have nothing measurable
 * yet, so they derive Green with an explicit reason. This is the dimension the
 * PM is most likely to override, which is fine - that's what overrides are for.
 */
export function deriveBenefit(project: Project, month: string): Signal {
  const ledger = project.monthlyLedger
  if (!ledger || Object.keys(ledger).length === 0) {
    return { rag: 'green', reason: 'Business case not yet measured' }
  }

  const [year, mth] = month.split('-').map(Number)
  let plan = 0
  let actual = 0
  for (const key of Object.keys(ledger)) {
    const [ky, km] = key.split('-').map(Number)
    if (ky > year || (ky === year && km > mth)) continue // future months aren't due yet
    const entry = ledger[key]
    plan += entry.base
    actual += entry.base + entry.adjustments
  }

  if (plan <= 0) return { rag: 'green', reason: 'Business case not yet measured' }
  const pct = Math.round((actual / plan) * 100)
  if (pct < 80) return { rag: 'red', reason: `Hours at ${pct}% of plan` }
  if (pct < 95) return { rag: 'amber', reason: `Hours at ${pct}% of plan` }
  return { rag: 'green', reason: `Hours at ${pct}% of plan` }
}

const asAssessment = (s: Signal, prev?: RagAssessment): RagAssessment => ({
  derived: s.rag,
  derivedReason: s.reason,
  // An override is a statement about a specific month's position, so it is
  // deliberately NOT carried forward - the PM re-asserts it or the data wins.
  override: prev?.derived === s.rag ? prev?.override : undefined,
  overrideReason: prev?.derived === s.rag ? prev?.overrideReason : undefined,
})

/** Recompute all four dimensions. Called every time the editor opens so a
 *  half-finished draft always reflects the latest risk log, not a stale copy. */
export function deriveAll(project: Project, settings: Settings, month: string, prev?: ProjectReport) {
  return {
    scope: asAssessment(deriveScope(project, settings), prev?.scope),
    schedule: asAssessment(deriveSchedule(project, settings), prev?.schedule),
    cost: asAssessment(deriveCost(project, settings), prev?.cost),
    benefit: asAssessment(deriveBenefit(project, month), prev?.benefit),
  }
}

/** Suggested overall status: the worst of the four. Only ever a starting
 *  point - overall is the PM's judgement (HOCE reports Amber overall while
 *  Cost sits Red, which is a legitimate call about materiality). */
export const suggestOverall = (d: {
  scope: RagAssessment
  schedule: RagAssessment
  cost: RagAssessment
  benefit: RagAssessment
}): RagStatus => worstRag([d.scope, d.schedule, d.cost, d.benefit].map(effectiveRag))

// ── Prefill ──────────────────────────────────────────────────────────────────

const uid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 9)}`

/** Milestones completed inside the reporting month - the natural highlights. */
/** Open material risks proposed as lowlights, carrying `riskId` so the report
 *  links back to the register and we never propose the same risk twice. */
/**
 * Space that queued-but-blank editor rows will occupy once filled in.
 *
 * Blank bullets never render, so the slide's measured free space cannot see
 * them - and without a reservation, stacking empty rows and populating them
 * afterwards walked straight past the add guard. Each blank reserves one row;
 * the first blank ask also reserves the card it will bring with it.
 */
export function blankReservationPx(
  blanks: { highlights: number; lowlights: number; supportNeeded: number },
  asksHaveRealItems: boolean,
  rowPx: number,
  cardPx: number,
): number {
  const rows = blanks.highlights + blanks.lowlights + blanks.supportNeeded
  const askCardPremium = blanks.supportNeeded > 0 && !asksHaveRealItems ? cardPx - rowPx : 0
  return rows * rowPx + askCardPremium
}

/** The four list sections sharing the slide's right column, in slide order. */
export interface ListCounts {
  highlights: number
  lowlights: number
  risks: number
  supportNeeded: number
}

/**
 * Share the right column's space between the four lists.
 *
 * `trim` is how many items the slide has measured as not fitting. They are
 * removed bottom-up - asks first, then risks, lowlights, highlights - i.e. in
 * reverse slide order, so the column fills first-come-first-served from the
 * top. There is deliberately no per-section cap: a report that is all
 * highlights is fine, it just leaves no room for anything below.
 */
export function allocateListTrim(counts: ListCounts, trim: number): ListCounts {
  const visible = { ...counts }
  let left = Math.max(0, trim)
  for (const k of ['supportNeeded', 'risks', 'lowlights', 'highlights'] as const) {
    const cut = Math.min(visible[k], left)
    visible[k] -= cut
    left -= cut
  }
  return visible
}

/**
 * Open risks frozen into the report, worst first.
 *
 * Everything the slide shows is captured here rather than read live: risks are
 * closed and re-rated constantly, and re-opening June's report must still show
 * the risk picture June was signed off against.
 *
 * All open risks are included, not just the material ones that drive the RAG
 * derivation - a Green-rated risk is still worth a line on a governance slide,
 * it just should not move the headline.
 */
export function snapshotRisks(
  project: Project,
  labels: Record<string, string> = {},
): ReportRisk[] {
  return (project.risks ?? [])
    .filter(r => r.status === 'open')
    .sort((a, b) => effectiveRiskScore(b) - effectiveRiskScore(a))
    .map(r => {
      const override = labels[r.id]?.trim()
      return {
      id: r.id,
      title: override || r.title,
      // Only set when the wording was actually changed, so an untouched risk
      // does not carry a redundant copy of itself.
      sourceTitle: override && override !== r.title ? r.title : undefined,
      rag: (riskRag(r) ?? 'green') as ReportRisk['rag'],
      score: effectiveRiskScore(r),
      owner: r.owner?.name,
      category: r.category,
      }
    })
}

export function buildSnapshot(
  project: Project,
  month: string,
  opts: { riskLabels?: Record<string, string> } = {},
): ReportSnapshot {
  const year = month.slice(0, 4)
  const budget = project.budgetByYear?.[year]
  return {
    projectName: project.name,
    subPortfolio: project.subPortfolio,
    tier: project.tier,
    stage: project.stage,
    functionalVP: project.functionalVP?.name,
    functionalLead: project.functionalLead?.name,
    projectManager: project.projectManager?.name ?? project.createdBy?.name,
    startDate: project.startDate,
    // Falls back to the go-live commitment for projects predating endDate.
    endDate: project.endDate ?? project.dueDate,
    goLive: project.actualGoLive ?? project.dueDate,
    budgetYear: year,
    capex: budget?.capex ?? 0,
    opex: budget?.opex ?? 0,
    capexCommitted: budget?.capexCommitted,
    opexCommitted: budget?.opexCommitted,
    // Half-entered rows (no date, or no label) never reach the slide - a dot
    // with no label, or a label with nowhere to sit, both render as noise.
    milestones: (project.milestones ?? [])
      .filter(m => !!m.date && m.label.trim() !== '')
      .sort((a, b) => a.date.localeCompare(b.date)),
    risks: snapshotRisks(project, opts.riskLabels),
  }
}

export interface Prefill {
  draft: ProjectReport
  /** Set when narrative text was carried forward, so the editor can warn that
   *  it needs re-reading before submit. Stale copy silently republished is the
   *  classic monthly-report failure - carrying forward saves real time, but
   *  only if the PM is told it happened. */
  carriedFromMonth: string | null
}

/**
 * Build this month's draft from the project, optionally seeded by last month's
 * report. Narrative, why-line and support-needed carry forward (most of it is
 * still true); RAGs are always recomputed; highlights and lowlights are
 * re-proposed from milestones and the risk log.
 */
export function buildPrefill(
  project: Project,
  settings: Settings,
  month: string,
  previous?: ProjectReport | null,
): Prefill {
  const derived = deriveAll(project, settings, month, previous ?? undefined)

  // Risks are no longer proposed as lowlights - they have their own block on
  // the slide now, so doing both would print every risk twice. Lowlights carry
  // forward as the PM's own narrative; risk-sourced ones from older reports are
  // dropped, since the risk block supersedes them.
  const carriedLowlights = (previous?.lowlights ?? []).filter(b => !b.riskId)

  return {
    carriedFromMonth: previous ? previous.month : null,
    draft: {
      id: `${project.id}_${month}`,
      projectId: project.id,
      month,
      reportDate: new Date().toISOString().slice(0, 10),
      overall: previous?.overall ?? suggestOverall(derived),
      overallPrevious: previous?.overall,
      whyLine: previous?.whyLine ?? '',
      narrative: previous?.narrative ?? '',
      ...derived,
      // Highlights are always hand-written: there is no maintained per-project
      // source to propose them from.
      highlights: [],
      lowlights: carriedLowlights.map(b => ({ ...b, id: uid('ll') })),
      supportNeeded: (previous?.supportNeeded ?? []).map(b => ({ ...b, id: uid('sn') })),
      snapshot: buildSnapshot(project, month, { riskLabels: previous?.riskLabels }),
      hideRisks: previous?.hideRisks,
      // Rewording carries forward: a risk phrased for the board last month
      // almost certainly needs the same phrasing this month.
      riskLabels: previous?.riskLabels,
      state: 'draft',
    },
  }
}

/** Sorted milestone list for the timeline. */
export const sortedMilestones = (ms: Milestone[]): Milestone[] =>
  [...ms].sort((a, b) => a.date.localeCompare(b.date))

// ── Timeline layout ──────────────────────────────────────────────────────────

export interface TimelinePoint {
  milestone: Milestone
  /** Dot position across the track, 0-1. Always the true date. */
  x: number
  /** Centre the label is actually drawn at, 0-1. Differs from `x` only for
   *  milestones near either end, where a centred label would run off the
   *  canvas. Rows are packed against this, not `x`, so nudging a label inwards
   *  can never push it into its neighbour. */
  labelX: number
  /** Display text, wrapped here rather than at render time so the width the
   *  layout reserves is the width actually drawn. One entry per rendered line. */
  lines: string[]
  /** Label row, 0 = nearest the track. Rows exist to stop labels colliding. */
  row: number
}

export interface TimelineLayout {
  points: TimelinePoint[]
  ticks: { x: number; label: string }[]
  /** Position of the report date, or null when it falls outside the window. */
  todayX: number | null
  rows: number
  /** Height of each row in text lines, so the caller can stack rows without
   *  reserving the worst case for every one of them. */
  rowLines: number[]
}

const monthsBetween = (from: Date, to: Date): number =>
  (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())

/**
 * Place milestones on a month axis and assign each label a row so they don't
 * overlap.
 *
 * The window is derived from the data rather than the full project span: HOCE
 * runs to Dec 2027 but every milestone lands by Nov 2026, and stretching the
 * axis to the contract end would squash them all into the left third. Start of
 * the earliest month to end of the latest keeps the dots readable.
 *
 * Rows are packed by measuring each label, not by a fixed minimum gap. A fixed
 * gap is wrong in both directions at once: "Q4" wastes a row it does not need,
 * while "Complete Stage 3 Design review" still collides. Each label is treated
 * as an interval around its drawn centre and dropped into the first row where
 * it clears the previous label's right edge.
 *
 * Widths are estimated in track fractions rather than pixels so the maths holds
 * as the SVG scales; the caller passes `charWidth` derived from its own
 * geometry so the estimate tracks the font size actually rendered.
 */
/**
 * Greedy word wrap for a milestone label.
 *
 * Wrapping beats truncating on both counts: the label reads in full, and a
 * two-line label is roughly half the width of the same text on one line, so it
 * collides with its neighbours far less. Only past `maxLines` is anything cut.
 */
export function wrapLabel(text: string, maxChars: number, maxLines: number): string[] {
  const words = (text ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ['']

  const lines: string[] = []
  let current = ''
  let dropped = false

  const push = (line: string): boolean => {
    if (lines.length >= maxLines) { dropped = true; return false }
    lines.push(line)
    return true
  }

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (candidate.length <= maxChars) { current = candidate; continue }
    if (current && !push(current)) { current = ''; break }
    current = word
    // A single word wider than the line gets hard-broken rather than
    // overflowing the slide.
    while (current.length > maxChars) {
      if (!push(current.slice(0, maxChars))) { current = ''; break }
      current = current.slice(maxChars)
    }
    if (lines.length >= maxLines && current) { dropped = true; current = ''; break }
  }
  if (current) push(current)

  if (dropped && lines.length > 0) {
    const last = lines[lines.length - 1]
    lines[lines.length - 1] =
      last.length >= maxChars ? `${last.slice(0, maxChars - 1)}\u2026` : `${last}\u2026`
  }
  return lines.length > 0 ? lines : ['']
}

export function layoutTimeline(
  milestones: Milestone[],
  opts: {
    reportDate?: string
    maxTicks?: number
    /** Track fraction one character occupies at the rendered font size. */
    charWidth?: number
    /** Characters per wrapped line. */
    maxLabelChars?: number
    /** Lines a label may wrap to before it is cut. */
    maxLabelLines?: number
    /** Clear space required between two labels, as a track fraction. */
    labelGap?: number
  } = {},
): TimelineLayout {
  const {
    reportDate,
    maxTicks = 8,
    // Defaults match the slide: a 1128px track at 10.5px Danone One bold.
    charWidth = 6 / 1128,
    maxLabelChars = 22,
    maxLabelLines = 2,
    labelGap = 8 / 1128,
  } = opts

  const sorted = sortedMilestones(milestones.filter(m => !!m.date))
  if (sorted.length === 0) return { points: [], ticks: [], todayX: null, rows: 1, rowLines: [] }

  const dates = sorted.map(m => new Date(m.date))
  const first = dates[0]
  const last = dates[dates.length - 1]
  // Snap to month boundaries, then guarantee at least a 2-month window so a
  // single milestone (or several in one month) doesn't divide by zero.
  const from = new Date(first.getFullYear(), first.getMonth(), 1)
  const to = new Date(last.getFullYear(), last.getMonth() + 1, 1)
  const totalMonths = Math.max(2, monthsBetween(from, to))

  const fracFor = (d: Date): number => {
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
    const months = monthsBetween(from, d) + (d.getDate() - 1) / daysInMonth
    return Math.min(1, Math.max(0, months / totalMonths))
  }

  // Right edge of the last label placed in each row, so a new label can find
  // the first row it clears. Left-to-right order means earlier milestones keep
  // row 0, which reads more naturally than a balanced distribution.
  const rowRightEdge: number[] = []
  const rowLines: number[] = []
  const points: TimelinePoint[] = sorted.map(m => {
    const x = fracFor(new Date(m.date))
    const lines = wrapLabel(m.label, maxLabelChars, maxLabelLines)
    // A wrapped label is only as wide as its longest line.
    const widest = Math.max(...lines.map(l => l.length))
    const half = (widest * charWidth) / 2
    // Pull labels at either end inwards so they stay on the canvas. The dot
    // stays put, so the leader line still points at the true date.
    const labelX = Math.min(1 - half, Math.max(half, x))
    let row = rowRightEdge.findIndex(right => labelX - half >= right + labelGap)
    if (row === -1) row = rowRightEdge.length
    rowRightEdge[row] = labelX + half
    rowLines[row] = Math.max(rowLines[row] ?? 1, lines.length)
    return { milestone: m, x, labelX, lines, row }
  })

  // Aim for <= maxTicks month labels, stepping in whole months.
  const step = Math.max(1, Math.ceil(totalMonths / maxTicks))
  const ticks: { x: number; label: string }[] = []
  for (let i = 0; i <= totalMonths; i += step) {
    const d = new Date(from.getFullYear(), from.getMonth() + i, 1)
    ticks.push({
      x: i / totalMonths,
      label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }).toUpperCase(),
    })
  }

  let todayX: number | null = null
  if (reportDate) {
    const d = new Date(reportDate)
    if (!Number.isNaN(d.getTime()) && d >= from && d <= to) todayX = fracFor(d)
  }

  return { points, ticks, todayX, rows: Math.max(1, rowRightEdge.length), rowLines }
}

export { monthKey }
